// Comprime imágenes en el navegador antes de subirlas.
// - Redimensiona a MAX_DIMENSION en el lado más largo (manteniendo proporción).
// - Re-codifica a JPEG con calidad alta (visualmente sin pérdida).
// - Los PNG con transparencia se mantienen en PNG (solo se redimensionan).
// - Si el resultado no es más liviano que el original, se devuelve el original.

const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.85;
const SKIP_BELOW_BYTES = 300 * 1024;
const COMPRESSIBLE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    img.src = url;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const hasTransparency = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  const { data } = ctx.getImageData(0, 0, width, height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
};

const replaceExtension = (name: string, extension: string) => {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.substring(0, dot) : name) + extension;
};

export const compressImage = async (file: File): Promise<File> => {
  if (!file || !COMPRESSIBLE_TYPES.includes(file.type)) return file;

  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    if (scale === 1 && file.size <= SKIP_BELOW_BYTES) return file;

    const width = Math.round(img.naturalWidth * scale);
    const height = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);

    const keepPng = file.type === "image/png" && hasTransparency(ctx, width, height);
    let blob: Blob | null;
    if (keepPng) {
      blob = await canvasToBlob(canvas, "image/png");
    } else {
      // Fondo blanco por si el origen tenía canal alfa (JPEG no lo soporta).
      ctx.globalCompositeOperation = "destination-over";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);
    }

    if (!blob || blob.size >= file.size) return file;

    const type = keepPng ? "image/png" : "image/jpeg";
    const name = keepPng ? file.name : replaceExtension(file.name, ".jpg");
    return new File([blob], name, { type, lastModified: Date.now() });
  } catch (error) {
    console.log("No se pudo comprimir la imagen, se sube la original", error);
    return file;
  }
};

// Reemplaza el campo "file" de un FormData por su versión comprimida.
export const compressFormImage = async (form: FormData, field = "file") => {
  const file = form.get(field);
  if (file instanceof File) {
    form.set(field, await compressImage(file));
  }
  return form;
};
